/**
 * Per-frame model of everything in the scene: true positions (km) and display positions (du, float64).
 * three.js objects are positioned from here, relative to the focus point (floating origin).
 *
 * Sprint 3: bodies from HORIZONS tables (Titan relative to Saturn, Arrokoth around the Sun; shown only inside their
 * coverage windows, S3-06), moons with nested bubbles (S3-07), spacecraft whose trajectory may arrive later (lazy
 * loading, S3-03) and a life cycle per spacecraft (D11).
 */
import type { BodyTable } from '../core/bodyEphemeris';
import type { BodyData, MissionData } from '../core/data';
import {
  BodyTrack,
  COARSE_TRACK_BODIES,
  COARSE_TRACK_STEP_DAYS,
  EphemerisCache,
  type EphemerisBodyId,
  ORBIT_RANGE_AU,
  type RotatingBodyId,
  isEphemerisBody,
  plutoKepler,
} from '../core/ephemeris';
import { AU_KM } from '../core/units';
import { type MappedBody, SpatialMapping } from '../core/mapping';
import { surfaceToInertial } from '../core/bodyFrame';
import { type LifeCycle, type LifeState, hasSiteMarker, isShown, lifeState, parseEnd } from '../core/missionState';
import { type SymbolicOrbit, symbolicOrbitOffset } from '../core/symbolicOrbit';
import {
  ANALYTIC_MOONS,
  ANALYTIC_MOON_PARENT,
  type AnalyticMoonId,
  MoonTrack,
  analyticMoonOffset,
  analyticMoons,
  galileanMoons,
  isAnalyticMoon,
} from '../core/moons';
import { OVERVIEW, type ScaleProfile } from '../core/scaleProfiles';
import { parseUtc, tdbDaysToUtcMs } from '../core/time';
import { type MoonBand, type Trajectory, createState, type SpacecraftState } from '../core/trajectory';
import { type Vec3, copy, length, vec3 } from '../core/vec3';

export interface WorldBody {
  data: BodyData;
  mapped: MappedBody;
  /** True heliocentric position, km. */
  p: Vec3;
  /** Display position, du. */
  d: Vec3;
  /** Display radius R′, du. */
  r: number;
  /** Moons: the parent planet; positions come from the moon theory or table relative to it. */
  parent?: WorldBody;
  /** Moons: offset from the parent, km. */
  q?: Vec3;
  /** False while the body has no position (a HORIZONS table outside its coverage, or not loaded yet). */
  active: boolean;
  /** `ephemeris: horizons` bodies: their table once loaded. */
  table?: BodyTable | null;
}

export interface WorldSpacecraft {
  id: string;
  name: string;
  missionId: string;
  color: string;
  /** Null until the trajectory file has been loaded (S3-03). */
  trajectory: Trajectory | null;
  state: SpacecraftState;
  /** Shown this frame: trajectory loaded, launched, not ended. */
  visible: boolean;
  /** Display position, du. */
  d: Vec3;
  /** Innermost body whose bubble the spacecraft is in (a moon inside a planet's bubble, else the planet), if any. */
  bubble: string | null;
  /** Launch time, UTC ms (the spacecraft's launch event, else the mission's launch date or its separation). */
  launchMs: number;
  life: LifeCycle;
  lifeState: LifeState;
  /** Spacecraft type from the data (icon = type, Concept §1). */
  type: string;
  /**
   * Display position of the end marker (D11), du: the trajectory's end while `lifeState` is 'ended', the landing site
   * while a site marker stays (landed, ascending, docked; S4-11).
   */
  end: Vec3;
  /** Whether the end marker is shown this frame. */
  endVisible: boolean;
  /**
   * Landers: the body landed on, and the landing point in that body's synchronous frame (see landingFrame) — or, on a
   * body with a known rotation (the Moon, the planets), the site's latitude and longitude in its IAU frame (D9).
   */
  landing?: { body: string; local: Vec3 | null; site?: { lat: number; lon: number } };
  /** Tier B: a symbolic orbit around the Earth instead of a trajectory (S3-21). */
  symbolic?: SymbolicOrbit;
  /** Reconstructed from key points, not measured (S4-08): the trail is drawn dotted (S4-09, D8). */
  reconstructed: boolean;
}

/** Bodies with a known rotation (the planets' and the Moon's IAU models): landing sites by latitude and longitude. */
function hasSurfaceFrame(body: string): boolean {
  return isEphemerisBody(body) || body === 'moon';
}

/** Finite-difference step for symbolic orbits' velocity, days (1 s). */
const SYMBOLIC_DT = 1 / 86_400;

export class World {
  readonly mapping: SpatialMapping;
  readonly bodies: WorldBody[];
  readonly bodyById = new Map<string, WorldBody>();
  readonly spacecraft: WorldSpacecraft[] = [];
  readonly spacecraftById = new Map<string, WorldSpacecraft>();
  readonly ephemeris = new EphemerisCache();
  tdb = Number.NaN;
  /** Bodies positioned on their own (Sun, planets, Pluto, Arrokoth) and moons (positioned relative to a parent). */
  readonly planets: WorldBody[];
  readonly moons: WorldBody[];
  private readonly moonOffsets = Object.fromEntries(ANALYTIC_MOONS.map((id) => [id, vec3()])) as Record<
    AnalyticMoonId,
    Vec3
  >;
  private readonly tracks = new Map<string, BodyTrack>();
  /** Earth's Moon for bulk lookups (trail sampling), S4-15. */
  private readonly moonTrack = new MoonTrack();
  private readonly moonTrackCoarse = new MoonTrack(MoonTrack.COARSE_STEP);
  /** Current heliocentric position of an active body (undefined when hidden) — the map M uses it. */
  readonly bodyPosFn: (id: string) => Vec3 | undefined;
  /** Exact body positions at any time (trajectory evaluation in the frame). */
  private readonly bodyPosAtFn: (id: string, t: number) => Vec3;
  /** Fast body positions at any time (Hermite tracks), for bulk trajectory line sampling. */
  readonly bodyAtFastFn: (id: string, t: number) => Vec3;
  private readonly zero = vec3();
  private readonly sampleCache = new EphemerisCache();
  private readonly tmpAt = vec3();
  private readonly tmpFast = vec3();
  private readonly tmpRel = vec3();
  private readonly tmpLandQ = vec3();
  private readonly tmpLandV = vec3();

  constructor(bodies: readonly BodyData[], profile: ScaleProfile) {
    this.bodies = bodies
      .filter((b) => isEphemerisBody(b.id) || isAnalyticMoon(b.id) || b.ephemeris === 'horizons')
      .map((data) => ({
        data,
        mapped: { id: data.id, radius: data.radius, bubbleRadius: data.bubbleRadius ?? 0 },
        p: vec3(),
        d: vec3(),
        r: 0,
        active: data.ephemeris !== 'horizons',
        ...(data.ephemeris === 'horizons' ? { table: null } : {}),
      }));
    for (const b of this.bodies) this.bodyById.set(b.data.id, b);
    for (const b of this.bodies) {
      const parent = b.data.parent && b.data.parent !== 'sun' ? this.bodyById.get(b.data.parent) : undefined;
      if (!parent) continue;
      b.parent = parent;
      b.q = vec3();
      b.mapped.parent = parent.data.id;
      if (isAnalyticMoon(b.data.id)) this.moonOffsets[b.data.id] = b.q;
    }
    this.planets = this.bodies.filter((b) => !b.parent);
    this.moons = this.bodies.filter((b) => b.parent);
    this.mapping = new SpatialMapping(
      this.bodies.map((b) => b.mapped),
      profile,
    );
    this.bodyPosFn = (id) => {
      const b = this.bodyById.get(id);
      return b?.active ? b.p : undefined;
    };
    this.bodyPosAtFn = (id, t) => this.bodyAt(id, t, this.ephemeris, this.tmpAt);
    this.bodyAtFastFn = (id, t) => this.bodyAtFast(id, t, this.tmpFast);
  }

  /** Installs a body's HORIZONS table once it has loaded (S3-06). */
  setBodyTable(id: string, table: BodyTable): void {
    const b = this.bodyById.get(id);
    if (b) b.table = table;
    this.bands.clear();
  }

  private readonly bands = new Map<string, MoonBand[]>();

  /** Bands around the planets' orbits (heliocentric cruise segments): ±(orbit range + 2 bubbles), ≤ 1/20 bubble. */
  private planetBands(): MoonBand[] {
    const out: MoonBand[] = [];
    for (const b of this.planets) {
      const range = ORBIT_RANGE_AU[b.data.id as keyof typeof ORBIT_RANGE_AU];
      if (!range || b.mapped.bubbleRadius <= 0) continue;
      const [lo, hi] = [range[0] * AU_KM, range[1] * AU_KM];
      out.push({
        orbit: (lo + hi) / 2,
        band: (hi - lo) / 2 + 2 * b.mapped.bubbleRadius,
        maxChord: b.mapped.bubbleRadius / 20,
      });
    }
    return out;
  }

  /**
   * Bands around the orbits of `planet`'s moons with nested bubbles, where trajectory lines need dense samples
   * (Trajectory.sampleTimes): ±2 bubble radii of the Overview mapping, segments ≤ 1/10 bubble radius. Realistic needs
   * none (its mapping is the identity), so Overview's bubbles are used whatever the current mode.
   */
  moonBands(planet: string): readonly MoonBand[] {
    let list = this.bands.get(planet);
    if (list) return list;
    if (planet === 'sun') {
      this.bands.set(planet, (list = this.planetBands()));
      return list;
    }
    list = [];
    const p = this.bodyById.get(planet);
    const overview = new SpatialMapping(
      this.bodies.map((b) => b.mapped),
      { ...OVERVIEW },
    );
    for (const moon of p ? this.moons.filter((m) => m.parent === p && m.mapped.bubbleRadius > 0) : []) {
      const q = vec3();
      let orbit = moon.data.orbitRadius ?? 0;
      if (orbit > 0) {
        // from the build (mean distance)
      } else if (isAnalyticMoon(moon.data.id)) {
        const offsets = Object.fromEntries(ANALYTIC_MOONS.map((id) => [id, vec3()])) as Record<AnalyticMoonId, Vec3>;
        analyticMoons(0, offsets);
        orbit = length(offsets[moon.data.id]);
      } else if (moon.table) {
        const [t0] = moon.table.windows()[0] ?? [0];
        if (moon.table.at(t0, q)) orbit = length(q);
      }
      if (orbit <= 0) continue;
      const rBub = overview.moonBubbleRadius(moon.mapped, p!.mapped, length(p!.p) || 5 * 149_597_870.7, orbit);
      list.push({ orbit, band: 2 * rBub, maxChord: 0.1 * rBub });
    }
    this.bands.set(planet, list);
    return list;
  }

  /** Registers a spacecraft (its trajectory may follow later, see setTrajectory). */
  addSpacecraft(
    mission: MissionData,
    scId: string,
    color: string,
    trajectory: Trajectory | null = null,
  ): WorldSpacecraft {
    const sc = mission.spacecraft.find((s) => s.id === scId);
    const launchEvent = mission.events.find((e) => e.type === 'launch' && (e.spacecraft ?? scId) === scId)?.date;
    const launchMs = parseUtc(launchEvent ?? sc?.separation ?? mission.launch.date);
    const entry: WorldSpacecraft = {
      id: scId,
      name: sc?.name ?? mission.name.en,
      missionId: mission.id,
      color,
      trajectory: null,
      state: createState(),
      visible: false,
      d: vec3(),
      bubble: null,
      launchMs,
      life: {
        launch: launchMs,
        end: parseEnd(mission.end),
        coverage: null,
        // a lander that lifts off again and rejoins its parent (S4-11, D10)
        ...(sc?.landing && sc.liftoff
          ? {
              landing: parseUtc(sc.landing.date),
              liftoff: parseUtc(sc.liftoff),
              ...(sc.rejoin ? { rejoin: parseUtc(sc.rejoin) } : {}),
            }
          : {}),
      },
      lifeState: 'before-launch',
      type: sc?.type ?? 'flyby',
      end: vec3(),
      endVisible: false,
      reconstructed: sc?.trajectory.quality === 'reconstructed',
      ...(sc?.landing
        ? {
            landing: {
              body: sc.landing.body,
              local: null,
              ...(sc.landing.lat !== undefined && sc.landing.lon !== undefined && hasSurfaceFrame(sc.landing.body)
                ? { site: { lat: sc.landing.lat, lon: sc.landing.lon } }
                : {}),
            },
          }
        : {}),
      ...(sc?.symbolicOrbit ? { symbolic: sc.symbolicOrbit } : {}),
    };
    if (entry.symbolic) {
      // from launch to the mission's end (re-entry, landing)
      entry.life.coverage = [launchMs, entry.life.end ?? Infinity];
    }
    this.spacecraft.push(entry);
    this.spacecraftById.set(scId, entry);
    if (trajectory) this.setTrajectory(scId, trajectory);
    return entry;
  }

  /** Installs a loaded trajectory (S3-03). */
  setTrajectory(scId: string, trajectory: Trajectory): void {
    const sc = this.spacecraftById.get(scId);
    if (!sc) return;
    sc.trajectory = trajectory;
    const [a, b] = trajectory.coverage();
    sc.life.coverage = [tdbDaysToUtcMs(a), tdbDaysToUtcMs(b)];
  }

  /** Display position (du) of a true heliocentric point with the current body positions — the map M right now. */
  mapNow(p: Vec3, out: Vec3): Vec3 {
    return this.mapping.mapPoint(p, this.bodyPosFn, out);
  }

  /** True heliocentric body position at an arbitrary time (separate cache; zero if unknown then). */
  bodyAt(id: string, t: number, cache: EphemerisCache = this.sampleCache, out: Vec3 = vec3()): Vec3 {
    if (isEphemerisBody(id)) return copy(out, cache.get(id as EphemerisBodyId, t));
    if (isAnalyticMoon(id)) {
      // the Moon for Moon-centred trajectory segments (S4-08): planet + offset
      analyticMoonOffset(id, t, out);
      const pp = cache.get(ANALYTIC_MOON_PARENT[id], t);
      out.x += pp.x;
      out.y += pp.y;
      out.z += pp.z;
      return out;
    }
    const b = this.bodyById.get(id);
    if (b?.table && b.table.at(t, out)) {
      if (b.parent) {
        const pp = cache.get(b.parent.data.id as EphemerisBodyId, t);
        out.x += pp.x;
        out.y += pp.y;
        out.z += pp.z;
      }
      return out;
    }
    return copy(out, this.zero);
  }

  /** Like bodyAt, from Hermite tracks between daily nodes (< 2 km off; for the trajectory lines). */
  private bodyAtFast(id: string, t: number, out: Vec3): Vec3 {
    const track = (bid: string): BodyTrack => {
      let tr = this.tracks.get(bid);
      if (!tr) this.tracks.set(bid, (tr = new BodyTrack(bid as EphemerisBodyId)));
      return tr;
    };
    if (isEphemerisBody(id)) return track(id).at(t, out);
    if (isAnalyticMoon(id)) {
      // not from the planets' daily nodes: a trail 100 km above the Moon must not wobble by their kilometres — the
      // Moon from its hourly track (< 1 m), Jupiter's moons exact
      if (id === 'moon') this.moonTrack.at(t, out);
      else analyticMoonOffset(id, t, out);
      const pp = track(ANALYTIC_MOON_PARENT[id]).at(t, this.tmpRel);
      out.x += pp.x;
      out.y += pp.y;
      out.z += pp.z;
      return out;
    }
    const b = this.bodyById.get(id);
    if (b?.table && b.table.at(t, out)) {
      if (b.parent) {
        const pp = track(b.parent.data.id).at(t, this.tmpRel);
        out.x += pp.x;
        out.y += pp.y;
        out.z += pp.z;
      }
      return out;
    }
    return copy(out, this.zero);
  }

  private analyticT = Number.NaN;
  private readonly analyticAt = Object.fromEntries(ANALYTIC_MOONS.map((id) => [id, vec3()])) as Record<
    AnalyticMoonId,
    Vec3
  >;

  /**
   * A planet-level body's true heliocentric position at time `t` (fast tracks; for trajectory lines), written into
   * `out`. False when it has no position then (a HORIZONS table outside its coverage).
   */
  planetAt(b: WorldBody, t: number, out: Vec3): boolean {
    if (b.table !== undefined) return !!b.table && b.table.at(t, out);
    this.bodyAtFast(b.data.id, t, out);
    return true;
  }

  /**
   * An outer planet's position from a coarse track, within COARSE_TRACK_ERROR_KM, for a quick distance test before
   * planetAt (S4-15: Pluto's daily nodes were a fifth of the trails' cost). False for the other bodies.
   */
  planetAtCoarse(b: WorldBody, t: number, out: Vec3): boolean {
    const id = b.data.id as EphemerisBodyId;
    if (b.table !== undefined || !COARSE_TRACK_BODIES.has(id)) return false;
    let tr = this.coarseTracks.get(id);
    if (!tr) this.coarseTracks.set(id, (tr = new BodyTrack(id, COARSE_TRACK_STEP_DAYS)));
    tr.at(t, out);
    return true;
  }

  private readonly coarseTracks = new Map<string, BodyTrack>();

  /**
   * Pluto's rough position (its Kepler orbit, within PLUTO_KEPLER_ERROR_KM) for a test before planetAtCoarse, whose
   * first node integrates an 80-year ephemeris segment (S4-03 part 6). False for every other body.
   */
  planetAtRough(b: WorldBody, t: number, out: Vec3): boolean {
    if (b.data.id !== 'pluto' || b.table !== undefined) return false;
    plutoKepler(t, out);
    return true;
  }

  /**
   * Earth's Moon's offset from Earth from a coarse track, within MoonTrack.COARSE_ERROR_KM, for a quick distance test
   * before moonOffsetAt (S4-15). False for the other moons.
   */
  moonOffsetAtCoarse(b: WorldBody, t: number, out: Vec3): boolean {
    if (b.data.id !== 'moon' || b.table !== undefined) return false;
    this.moonTrackCoarse.at(t, out);
    return true;
  }

  /** A moon's offset from its planet at time `t` (km), written into `out`; false when it has none then. */
  moonOffsetAt(b: WorldBody, t: number, out: Vec3): boolean {
    if (b.table !== undefined) return !!b.table && b.table.at(t, out);
    if (!isAnalyticMoon(b.data.id)) return false;
    // Earth's Moon from its track, Jupiter's four without it (S4-15: every line sample near Earth computed all five
    // with the full theories)
    if (b.data.id === 'moon') {
      this.moonTrack.at(t, out);
      return true;
    }
    if (t !== this.analyticT) {
      galileanMoons(t, this.analyticAt);
      this.analyticT = t;
    }
    copy(out, this.analyticAt[b.data.id]);
    return true;
  }

  /** Recomputes all positions for time `tdb` with the mapping's current profile. */
  update(tdb: number): void {
    this.tdb = tdb;
    const m = this.mapping;
    for (const b of this.planets) {
      if (b.table !== undefined) {
        // a heliocentric HORIZONS body (Arrokoth): shown inside its table's windows only
        b.active = !!b.table && b.table.at(tdb, b.p);
        if (!b.active) continue;
      } else copy(b.p, this.ephemeris.get(b.data.id as EphemerisBodyId, tdb));
      m.mapGlobal(b.p, b.d);
      b.r = m.displayRadius(b.mapped);
    }
    if (this.moons.some((b) => isAnalyticMoon(b.data.id))) analyticMoons(tdb, this.moonOffsets);
    for (const b of this.moons) {
      const parent = b.parent!;
      const q = b.q!;
      if (b.table !== undefined) {
        b.active = !!b.table && b.table.at(tdb, q);
        if (!b.active) continue;
      }
      b.p.x = parent.p.x + q.x;
      b.p.y = parent.p.y + q.y;
      b.p.z = parent.p.z + q.z;
      m.mapRelative(parent.mapped, parent.p, q, b.d);
      b.r = m.displayRadius(b.mapped);
    }
    const ms = tdbDaysToUtcMs(tdb);
    for (const sc of this.spacecraft) {
      sc.lifeState = lifeState(sc.life, ms);
      if (sc.symbolic) {
        this.placeSymbolic(sc, ms);
        continue;
      }
      sc.visible = !!sc.trajectory && isShown(sc.lifeState) && sc.trajectory.stateAt(tdb, this.bodyPosAtFn, sc.state);
      sc.endVisible = false;
      // the descent stage stays on its site from touchdown on (S4-11)
      if (hasSiteMarker(sc.lifeState)) this.placeSite(sc);
      if (!sc.visible) {
        sc.bubble = null;
        if (sc.lifeState === 'ended' && sc.trajectory) this.placeEnd(sc);
        continue;
      }
      const frameBody = sc.state.frame !== 'sun' ? this.bodyById.get(sc.state.frame) : undefined;
      if (frameBody?.active && !frameBody.parent) {
        // encounter data relative to a planet: the planet's bubble, or a moon's nested in it
        const moon = m.moonBubbleOf(frameBody.mapped, frameBody.p, sc.state.q, this.bodyPosFn);
        m.mapInPlanet(frameBody.mapped, frameBody.p, sc.state.q, this.bodyPosFn, sc.d);
        const rBub = m.bubbleRadius(frameBody.mapped, length(frameBody.p));
        sc.bubble = moon?.id ?? (length(sc.state.q) < rBub ? frameBody.data.id : null);
      } else {
        const chain = m.locate(sc.state.p, this.bodyPosFn);
        sc.bubble = chain.moon?.id ?? chain.planet?.id ?? null;
        m.mapPoint(sc.state.p, this.bodyPosFn, sc.d);
      }
    }
  }

  /** A Tier B spacecraft on its symbolic Earth orbit (S3-21): position, velocity (finite difference) and display. */
  private placeSymbolic(sc: WorldSpacecraft, ms: number): void {
    sc.endVisible = false;
    const earth = this.bodyById.get('earth');
    sc.visible = !!earth && (sc.lifeState === 'active' || sc.lifeState === 'silent');
    if (!sc.visible || !earth) {
      sc.bubble = null;
      return;
    }
    const days = (ms - sc.launchMs) / 86_400_000;
    const R = earth.mapped.radius;
    const st = sc.state;
    symbolicOrbitOffset(sc.symbolic!, R, days, st.q);
    const ahead = symbolicOrbitOffset(sc.symbolic!, R, days + SYMBOLIC_DT, this.tmpRel);
    st.w.x = (ahead.x - st.q.x) / SYMBOLIC_DT;
    st.w.y = (ahead.y - st.q.y) / SYMBOLIC_DT;
    st.w.z = (ahead.z - st.q.z) / SYMBOLIC_DT;
    st.p.x = earth.p.x + st.q.x;
    st.p.y = earth.p.y + st.q.y;
    st.p.z = earth.p.z + st.q.z;
    // heliocentric velocity: Earth's (central difference over ±1 min) plus the orbital one
    const tdb = this.tdb;
    const after = this.ephemeris.get('earth', tdb + 1 / 1440);
    const ax = after.x,
      ay = after.y,
      az = after.z;
    const before = this.ephemeris.get('earth', tdb - 1 / 1440);
    st.v.x = (ax - before.x) * 720 + st.w.x;
    st.v.y = (ay - before.y) * 720 + st.w.y;
    st.v.z = (az - before.z) * 720 + st.w.z;
    this.ephemeris.get('earth', tdb); // the cache holds one time: back to now for everyone else
    st.frame = 'earth';
    this.mapping.mapInPlanet(earth.mapped, earth.p, st.q, this.bodyPosFn, sc.d);
    sc.bubble = 'earth';
  }

  /**
   * The end marker (D11) at the trajectory's last point, mapped now. A lander's marker stays on the body it landed on:
   * its last position is kept in the body's synchronous frame (one face towards the planet, as Titan), so it turns
   * with the body (S3-18).
   */
  private placeEnd(sc: WorldSpacecraft): void {
    const traj = sc.trajectory!;
    const [, tEnd] = traj.coverage();
    const m = this.mapping;
    if (sc.landing?.site) {
      this.placeSite(sc);
      return;
    }
    if (sc.landing) {
      const body = this.bodyById.get(sc.landing.body);
      if (!body?.active || !body.parent || !body.table) return;
      if (!sc.landing.local) {
        // once: the landing point in the body's frame at the end of the trajectory
        const st = createState();
        if (!traj.stateAt(tEnd, this.bodyPosAtFn, st)) return;
        const q = vec3();
        const v = vec3();
        if (!body.table.at(tEnd, q, v)) return;
        const parentP = this.bodyAt(body.parent.data.id, tEnd);
        const rel = vec3(st.p.x - parentP.x - q.x, st.p.y - parentP.y - q.y, st.p.z - parentP.z - q.z);
        sc.landing.local = toFrame(rel, q, v);
      }
      const v = this.tmpLandV;
      const q = body.q!;
      body.table.at(this.tdb, this.tmpLandQ, v);
      const rel = fromFrame(sc.landing.local, q, v);
      // just above the surface: the mapping clamps to the display surface, so the marker sits on the enlarged body
      const r = Math.hypot(rel.x, rel.y, rel.z) || 1;
      const k = (body.mapped.radius * 1.001) / r;
      const p = vec3(body.p.x + rel.x * k, body.p.y + rel.y * k, body.p.z + rel.z * k);
      m.mapPoint(p, this.bodyPosFn, sc.end);
      sc.endVisible = true;
      return;
    }
    const st = createState();
    if (!traj.stateAt(tEnd, this.bodyPosAtFn, st)) return;
    m.mapPoint(st.p, this.bodyPosFn, sc.end);
    sc.endVisible = true;
  }

  /**
   * The landing site marker (S4-11, D9): latitude and longitude in the body's IAU frame, turning with the body, on
   * its display surface (as the end marker of a lander, see placeEnd).
   */
  private placeSite(sc: WorldSpacecraft): void {
    const site = sc.landing?.site;
    const body = sc.landing ? this.bodyById.get(sc.landing.body) : undefined;
    if (!site || !body?.active) return;
    const rel = surfaceToInertial(
      body.data.id as RotatingBodyId,
      body.mapped.radius,
      this.tdb,
      { lat: site.lat, lon: site.lon, alt: 0 },
      this.tmpLandQ,
    );
    // exactly on the surface, where the landed spacecraft's trajectory is: the mapping amplifies low altitudes, and
    // Huygens' 0.1 % (1.7 km here) put the marker 3 px off Eagle at close range
    const p = this.tmpLandV;
    p.x = body.p.x + rel.x;
    p.y = body.p.y + rel.y;
    p.z = body.p.z + rel.z;
    this.mapping.mapPoint(p, this.bodyPosFn, sc.end);
    sc.endVisible = true;
  }

  /** Display position of any focusable object (undefined while it is not shown). */
  displayOf(id: string): Vec3 | undefined {
    const b = this.bodyById.get(id);
    if (b) return b.active ? b.d : undefined;
    const sc = this.spacecraftById.get(id);
    return sc?.visible ? sc.d : undefined;
  }

  /** Display radius of a body's bubble edge (R′ + h(R_bub − R)), du; nested moon bubbles included. */
  displayBubbleRadius(id: string): number {
    const b = this.bodyById.get(id);
    if (!b || b.mapped.bubbleRadius <= 0 || !b.active) return 0;
    const m = this.mapping;
    if (b.parent) {
      const r = length(b.parent.p);
      const dm = length(b.q!);
      const rBub = m.moonBubbleRadius(b.mapped, b.parent.mapped, r, dm);
      return b.r + m.moonAltitude(b.mapped, b.parent.mapped, r, dm, rBub - b.mapped.radius);
    }
    const r = length(b.p);
    const rBub = m.bubbleRadius(b.mapped, r);
    return b.r + m.altitude(b.mapped, r, rBub - b.mapped.radius);
  }
}

/**
 * A body's synchronous frame from its position `q` and velocity `v` relative to its planet: x towards the planet,
 * z along the orbit normal, y completing the right-handed frame.
 */
function frameAxes(q: Vec3, v: Vec3): [Vec3, Vec3, Vec3] {
  const x = vec3(-q.x, -q.y, -q.z);
  const lx = Math.hypot(x.x, x.y, x.z);
  x.x /= lx;
  x.y /= lx;
  x.z /= lx;
  const z = vec3(q.y * v.z - q.z * v.y, q.z * v.x - q.x * v.z, q.x * v.y - q.y * v.x);
  const lz = Math.hypot(z.x, z.y, z.z);
  z.x /= lz;
  z.y /= lz;
  z.z /= lz;
  const y = vec3(z.y * x.z - z.z * x.y, z.z * x.x - z.x * x.z, z.x * x.y - z.y * x.x);
  return [x, y, z];
}

function toFrame(rel: Vec3, q: Vec3, v: Vec3): Vec3 {
  const [x, y, z] = frameAxes(q, v);
  return vec3(
    rel.x * x.x + rel.y * x.y + rel.z * x.z,
    rel.x * y.x + rel.y * y.y + rel.z * y.z,
    rel.x * z.x + rel.y * z.y + rel.z * z.z,
  );
}

function fromFrame(local: Vec3, q: Vec3, v: Vec3): Vec3 {
  const [x, y, z] = frameAxes(q, v);
  return vec3(
    local.x * x.x + local.y * y.x + local.z * z.x,
    local.x * x.y + local.y * y.y + local.z * z.y,
    local.x * x.z + local.y * y.z + local.z * z.z,
  );
}
